import { Button, DialogDescription, DialogFooter, DialogHeader, DialogTitle, FormCheckbox, FormInput, Heading, IconTick, ScrollArea, Text } from "@sentricmusic/ui";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useUIStateStore } from "@/store/uiStateStore";

import type { SelectedWriter, Writer, WriterCallbacks, WriterForSelection } from "./types";

type SelectExistingWritersModalProps = {
  availableWriters: Writer[];
  selectedWriters: SelectedWriter[];
  onWritersSelected: WriterCallbacks["onWritersSelected"];
  onClose: WriterCallbacks["onClose"];
  title?: string;
  description?: string;
  translationNamespace?: string;
};

export default function SelectExistingWritersModal({
  availableWriters,
  selectedWriters,
  onWritersSelected,
  onClose,
  title,
  description,
}: SelectExistingWritersModalProps) {
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const [tempSelectedWriters, setTempSelectedWriters] = useState<Set<string>>(() => new Set());
  const [searchQuery, setSearchQuery] = useState("");

  const { t } = useTranslation(["common", "writers"]);

  const handleWriterToggle = (writercode: string) => {
    setTempSelectedWriters((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(writercode)) {
        newSet.delete(writercode);
      }
      else {
        newSet.add(writercode);
      }
      return newSet;
    });
  };

  function getWriterDisplayName(writer: Writer) {
    if (writer?.writername?.trim())
      return writer.writername.trim();

    return [writer.firstname, writer.middlename, writer.lastname].filter(Boolean).join(" ");
  }

  // Filter out already selected writers and apply search
  const availableWritersFiltered = useMemo(() => {
    return availableWriters.filter((writer) => {
      // Filter out already selected writers
      const notAlreadySelected = !selectedWriters.some(selected => selected.writercode === writer.writercode);

      // Apply search filter
      if (!searchQuery.trim()) {
        return notAlreadySelected;
      }

      // Create display name for searching
      const displayName = getWriterDisplayName(writer);

      // Search in name and CAE/IPI number
      const searchLower = searchQuery.toLowerCase();
      const nameMatch = displayName.toLowerCase().includes(searchLower);
      const caeIpiMatch = writer.caeipinumber?.toLowerCase().includes(searchLower) ?? false;

      return notAlreadySelected && (nameMatch || caeIpiMatch);
    }).reverse();
  }, [availableWriters, selectedWriters, searchQuery]);

  const handleSelectAll = useCallback((selected: boolean) => {
    if (selected) {
      const allWriterCodes = availableWritersFiltered.map(writer => writer.writercode);
      setTempSelectedWriters(new Set(allWriterCodes));
    }
    else {
      setTempSelectedWriters(new Set());
    }
  }, [availableWritersFiltered]);

  const handleConfirm = () => {
    const writersToAdd: WriterForSelection[] = availableWriters
      .filter(writer => tempSelectedWriters.has(writer.writercode))
      .map((writer): WriterForSelection => {
        // Create display name from firstname/middlename/lastname if writername is not available
        const displayName = getWriterDisplayName(writer);

        return {
          writercode: writer.writercode,
          writername: displayName,
          caeipinumber: writer.caeipinumber ?? null,
          dateofbirth: writer.dateofbirth ?? null,
          writerexternalregistrationstatus: writer.writerexternalregistrationstatus ?? null,
        };
      });

    // Call the parent callback with the selected writers
    onWritersSelected?.(writersToAdd);

    toast.success(t("writers:added-writers", { count: writersToAdd.length }));
    onClose();
  };

  // If availableWriters is empty, show a message to load writers first
  if (availableWriters.length === 0) {
    return (
      <div className={`space-y-6 ${isTunecore ? "text-white" : ""}`}>
        <div>
          <Text as="p">
            {t("writers:no-writers-available")}
          </Text>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            label={t("writers:close")}
            onClick={onClose}
          />
        </DialogFooter>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${isTunecore ? "text-white" : ""}`}>
      <DialogHeader className="space-y-4 text-left">
        <DialogTitle asChild>
          <Heading as="h3" className={isTunecore ? "text-white" : ""}>
            {title !== null && title !== undefined && title.trim() !== "" ? title : t("writers:select-existing-writers")}
          </Heading>
        </DialogTitle>
        <DialogDescription asChild>
          <Text as="p">
            {description !== null && description !== undefined && description.trim() !== "" ? (
              <span>{description}</span>
            ) : (
              <>
                <span>{t("writers:select-existing-writers-description1")}</span>
                <br />
                <span>{t("writers:select-existing-writers-description2")}</span>
              </>
            )}
          </Text>
        </DialogDescription>
      </DialogHeader>

      {/* Stats and Select All */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Text as="p" className="">
            {t("writers:available-writers", { count: availableWritersFiltered.length })}
          </Text>
          <Text as="p" className="font-semibold ">
            {t("writers:writers-selected", { count: tempSelectedWriters.size })}
          </Text>
        </div>
        <div className="flex items-center">
          <FormCheckbox
            label={t("writers:select-all") ?? "Select All"}
            checked={availableWritersFiltered.length > 0 && tempSelectedWriters.size === availableWritersFiltered.length}
            onChange={e => handleSelectAll(e.target.checked)}
            className="w-4 h-4"
          />
        </div>
      </div>

      <FormInput
        label={t("writers:search-writers") ?? "Search Writers"}
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
        placeholder={t("writers:search-writers-placeholder") ?? "Search by name or CAE/IPI number..."}
      />
      {/* Writers List */}
      <ScrollArea className="h-[500px] border rounded-lg border-stroke-subtle">
        <div className="p-4 space-y-3">
          {availableWritersFiltered.length === 0 ? (
            <div className="py-8 text-center">
              <Text as="p" className={isTunecore ? "text-white" : "text-black"}>
                {t("writers:no-writers-found")}
              </Text>
            </div>
          ) : (
            availableWritersFiltered.map((writer) => {
              // Create display name from firstname/middlename/lastname if writername is not available
              const displayName = getWriterDisplayName(writer);

              return (
                <button
                  key={writer.writercode}
                  type="button"
                  className={`w-full p-4 border rounded-lg cursor-pointer transition-colors text-left ${
                    tempSelectedWriters.has(writer.writercode)
                      ? "bg-layer-raised border-stroke-subtle"
                      : isTunecore
                        ? "bg-[#282828] border-stroke-subtle hover:bg-layer-raised"
                        : "bg-white border-stroke-subtle hover:bg-primary-button-hover"
                  }`}
                  onClick={() => handleWriterToggle(writer.writercode)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <Text as="p" className={`font-semibold ${isTunecore ? "text-white" : ""}`}>
                        {displayName}
                      </Text>
                      <Text as="p" className={`text-sm ${isTunecore ? "text-white" : ""}`}>
                        {t("writers:cae-ipi") ?? "CAE/IPI"}
                        {": "}
                        {writer.caeipinumber ?? "N/A"}
                      </Text>
                    </div>
                    {tempSelectedWriters.has(writer.writercode) && (
                      <IconTick size="xl" className={`${isTunecore ? "stroke-white" : "stroke-black"}`} />
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </ScrollArea>

      <DialogFooter>
        <Button
          variant="darkSecondary"
          label={t("writers:cancel") ?? "Cancel"}
          onClick={onClose}
          className={isTunecore ? "border-layer-inverse text-white hover:bg-layer-inverse" : ""}
        />

        <Button
          variant={isTunecore ? "secondary" : "primary"}
          label={t("writers:add-writers", { count: tempSelectedWriters.size }) ?? `Add ${tempSelectedWriters.size} Writer(s)`}
          onClick={handleConfirm}
          disabled={tempSelectedWriters.size === 0}
        />
      </DialogFooter>
    </div>
  );
}
